import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type {
  DistributeVerificationInput,
  DistributeVerificationResult,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * distributeVerification callable hook (Sprint AZ、Phase 5 REQ-4.3.20〜25)。
 *
 * `useExportPosts` と同 pattern (idle / pending / success / error 4 状態)。
 * Hook 自体は status と call/reset を提供するだけで、UX 側 (form / ConfirmDialog / toast) は page に。
 */

export type UseDistributeVerificationState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: DistributeVerificationResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseDistributeVerificationReturn {
  state: UseDistributeVerificationState;
  call: (input: DistributeVerificationInput) => Promise<DistributeVerificationResult | null>;
  reset: () => void;
}

export function useDistributeVerification(): UseDistributeVerificationReturn {
  const [state, setState] = useState<UseDistributeVerificationState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(
    input: DistributeVerificationInput,
  ): Promise<DistributeVerificationResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<DistributeVerificationInput, DistributeVerificationResult>(
        functions,
        'distributeVerification',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
